import { Capacitor } from "@capacitor/core";
import { Box, Divider, Stack } from "@mui/material";
import { useCallback } from "react";
import { ColoursObj, drawerStore, MobileDrawerFrame, Typography, getContrastYIQ } from "@eventr/common-storybook";
import { openMapApp } from "utils";
import { useThemeContext } from "state/themeState/themeContext";
import { AppleMapsDark, AppleMapsLight, GoogleMaps, WazeDark, WazeLight } from "assets/svgImages/map-apps";
import i18n from "i18n/i18next";

/**
 * Shared hook that returns a function to open the map app selection drawer.
 *
 * @param query - The address or coordinates string to pass to the map app.
 *                 If omitted, the returned function accepts the query at call time.
 */
export function useMapSelector(query?: string) {
  const selectedColourScheme = useThemeContext((s) => s.selectedColourScheme);

  const openDrawer = useCallback(
    (q: string) => {
      const isWeb = !Capacitor.isNativePlatform();
      const showAppleMaps = Capacitor.getPlatform() === "ios" || isWeb;

      drawerStore().openDrawer({
        node: <MapAppDrawer query={q} showAppleMaps={showAppleMaps} selectedColourScheme={selectedColourScheme} />,
        onBackdropClick: () => drawerStore().closeDrawer(),
      });
    },
    [selectedColourScheme],
  );

  const openMapSelector = useCallback(
    (overrideQuery?: string) => {
      const q = overrideQuery ?? query;
      if (!q) return;
      openDrawer(q);
    },
    [openDrawer, query],
  );

  return openMapSelector;
}

/* ------------------------------------------------------------------ */
/*  Drawer UI                                                          */
/* ------------------------------------------------------------------ */

function MapAppDrawer({
  query,
  showAppleMaps,
  selectedColourScheme,
}: {
  query: string;
  showAppleMaps: boolean;
  selectedColourScheme: string;
}) {
  return (
    <MobileDrawerFrame title={i18n.t("addressRow.openInMaps")} minHeight="30vh" withCloseButton>
      <MapAppOptions query={query} showAppleMaps={showAppleMaps} selectedColourScheme={selectedColourScheme} />
    </MobileDrawerFrame>
  );
}

/* ------------------------------------------------------------------ */
/*  Map app option rows                                                */
/* ------------------------------------------------------------------ */

function MapAppOptions({
  query,
  showAppleMaps,
  selectedColourScheme,
}: {
  query: string;
  showAppleMaps: boolean;
  selectedColourScheme: string;
}) {
  const closeAndOpen = (app: "appleMaps" | "googleMaps" | "waze") => {
    drawerStore().closeDrawer();
    openMapApp(app, query);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        padding: "16px",
        gap: "8px"
      }}>
      {showAppleMaps && (
        <>
          <MapAppOption
            label={i18n.t("addressRow.appleMaps")}
            onClick={() => closeAndOpen("appleMaps")}
            icon={selectedColourScheme === "light" ? <AppleMapsLight /> : <AppleMapsDark />}
          />
          <Divider />
        </>
      )}
      <MapAppOption
        label={i18n.t("addressRow.googleMaps")}
        onClick={() => closeAndOpen("googleMaps")}
        icon={<GoogleMaps />}
      />
      <Divider />
      <MapAppOption
        label={i18n.t("addressRow.waze")}
        onClick={() => closeAndOpen("waze")}
        icon={selectedColourScheme === "light" ? <WazeLight /> : <WazeDark />}
      />
      <Divider />
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/*  Individual map app option row                                      */
/* ------------------------------------------------------------------ */

const MapAppOption = ({ label, onClick, icon }: { label: string; onClick: () => void; icon: React.ReactNode }) => {
  return (
    <Box onClick={onClick} sx={{ cursor: "pointer" }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: "12px"
        }}>
        {icon}
        <Typography variant="bodyRegular" color={getContrastYIQ(ColoursObj.backgrounds.secondary)}>
          {label}
        </Typography>
      </Stack>
    </Box>
  );
};
